import { useState } from 'react';
import { ArrowRight, MapPin, Phone, Mail, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from './consulting-data';

export const ConsultingContact = () => {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', need: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Veuillez remplir les champs obligatoires'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        subject: `Consulting - ${form.need || 'Non précisé'}`,
        message: form.message || `Besoin : ${form.need}`
      });
      toast.success('Demande envoyée ! Nous vous recontactons sous 24h.');
      setForm({ name: '', company: '', email: '', phone: '', need: '', message: '' });
    } catch { toast.error("Erreur lors de l'envoi."); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <section id="contact-consulting" className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] py-20" data-testid="contact-consulting">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[5fr_7fr] gap-16">
          <div>
            <div className="inline-flex items-center gap-2 text-[#F59E0B]/85 text-xs font-semibold tracking-[0.22em] uppercase mb-4">
              <span className="w-7 h-[1.5px] bg-[#F59E0B]/85" /> Parlons de votre situation
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-5">
              Un échange suffit souvent<br />pour <span className="italic text-[#F59E0B]">clarifier les enjeux.</span>
            </h2>
            <p className="text-sm text-white/55 leading-relaxed mb-10">Avant d'agir, un premier rendez-vous gratuit - en présentiel à Papeete ou en distanciel - permet de comprendre votre situation. Sans engagement.</p>
            <div className="space-y-4">
              {[
                { icon: MapPin, lbl: 'Adresse', val: '10 Rue Georges LAGARDE - 98714 Papeete' },
                { icon: Phone, lbl: 'Telephone', val: '87 73 78 15' },
                { icon: Mail, lbl: 'Email', val: 'contact@groupeaerhpolynesie.com' },
                { icon: MessageSquare, lbl: 'WhatsApp', val: '+689 87 73 78 15' }
              ].map(c => (
                <div key={c.lbl} className="flex gap-4 items-start">
                  <div className="w-9 h-9 bg-[#F59E0B]/12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-4 h-4 text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">{c.lbl}</p>
                    <p className="text-sm text-white font-medium mt-0.5">{c.val}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-10">
            <h3 className="text-lg font-bold text-white mb-1">Votre demande</h3>
            <p className="text-xs text-white/38 mb-7 leading-relaxed">Réponse sous 24h ouvrées. Toutes les situations sont traitees avec confidentialité.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/40 text-[10px] uppercase tracking-wider">Prenom & Nom *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Votre nom" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" required data-testid="form-name" />
                </div>
                <div>
                  <Label className="text-white/40 text-[10px] uppercase tracking-wider">Entreprise</Label>
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Votre structure" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" data-testid="form-company" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/40 text-[10px] uppercase tracking-wider">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="votre@email.com" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" required data-testid="form-email" />
                </div>
                <div>
                  <Label className="text-white/40 text-[10px] uppercase tracking-wider">Telephone</Label>
                  <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="87 XX XX XX" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" data-testid="form-phone" />
                </div>
              </div>
              <div>
                <Label className="text-white/40 text-[10px] uppercase tracking-wider">Votre besoin principal *</Label>
                <Select value={form.need} onValueChange={(v) => setForm({ ...form, need: v })}>
                  <SelectTrigger className="mt-1 bg-white/[0.06] border-white/10 text-white" data-testid="form-need"><SelectValue placeholder="- Selectionnez une option -" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pack-solo">Pack Solo - Création d'entreprise individuelle</SelectItem>
                    <SelectItem value="pack-pro">Pack Pro - Création avec investissement / financement</SelectItem>
                    <SelectItem value="prog90">Programme Croissance Serenite 90 jours</SelectItem>
                    <SelectItem value="audit">Audit RH & conformité (CPFP)</SelectItem>
                    <SelectItem value="conseil">Conseil RH opérationnel - situation spécifique</SelectItem>
                    <SelectItem value="structuration">Structuration & organisation RH</SelectItem>
                    <SelectItem value="externalisation">Externalisation de la fonction RH</SelectItem>
                    <SelectItem value="diagnostic">Je ne sais pas encore - je voudrais un diagnostic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/40 text-[10px] uppercase tracking-wider">Votre situation en quelques mots</Label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Décrivez votre contexte, vos contraintes..."
                  className="mt-1 w-full rounded-md bg-white/[0.06] border border-white/10 text-white placeholder:text-white/22 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B] resize-y"
                  data-testid="form-message" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold py-5" data-testid="form-submit">
                {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      <a href="https://api.whatsapp.com/send?phone=68987737815&text=Bonjour%20AERH%20Consulting%20!" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50"
        data-testid="whatsapp-btn" title="Contactez-nous sur WhatsApp">
        <MessageSquare className="w-6 h-6 text-white" />
      </a>
    </>
  );
};
